import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Play, Loader2, Key, HelpCircle, Lock, Brain, Database, HardDrive, Search, FileText, Sparkles, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { FileDropZone } from '@/components/FileDropZone';
import { OutputViewer } from '@/components/OutputViewer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Hint = ({ text }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger><HelpCircle className="w-3 h-3 text-muted-foreground/50 inline ml-1" /></TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const FieldLabel = ({ children, hint }) => (
  <Label className="text-xs flex items-center gap-1">{children}{hint && <Hint text={hint} />}</Label>
);

const ServiceLocked = ({ name }) => (
  <div className="p-6 text-center space-y-2 rounded-xl bg-secondary/10 border border-border/20">
    <Lock className="w-6 h-6 mx-auto text-muted-foreground/40" />
    <p className="text-sm text-muted-foreground">You need to subscribe to <strong>{name}</strong> to test it here.</p>
    <Button asChild size="sm" variant="outline" className="text-xs"><Link to="/dashboard/services">Go to Services</Link></Button>
  </div>
);

export default function Playground() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [tab, setTab] = useState('ml');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollStatus, setPollStatus] = useState('');
  const [pollElapsed, setPollElapsed] = useState(0);
  const [activeJobId, setActiveJobId] = useState(null);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const enabledSvcs = new Set((user?.enabled_services || []).filter(s => s.is_active).map(s => s.service));
  const hasSvc = (id) => enabledSvcs.has(id);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    setPolling(false);
    setPollStatus('');
    setActiveJobId(null);
  }, []);

  const cancelJob = useCallback(async () => {
    if (!activeJobId || !apiKey) return;
    try {
      await axios.delete(`${API_URL}/api/v1/jobs/${activeJobId}`, { headers: { 'X-API-Key': apiKey } });
      stopPolling();
      setResult(null);
      setError('Job cancelled');
      setLoading(false);
    } catch (err) {
      toast.error('Failed to cancel job');
    }
  }, [activeJobId, apiKey, stopPolling]);

  const call = useCallback(async (method, path, data, isForm = false) => {
    if (!apiKey) { toast.error('Enter your API key first'); return; }
    setLoading(true); setResult(null); setError(null); setLatency(null);
    stopPolling();
    const start = Date.now();
    try {
      const headers = { 'X-API-Key': apiKey };
      if (!isForm) headers['Content-Type'] = 'application/json';
      const res = method === 'GET'
        ? await axios.get(`${API_URL}/api/v1${path}`, { headers })
        : await axios.post(`${API_URL}/api/v1${path}`, data, { headers });

      // Detect async response (has job_id) — enter polling mode
      if (res.data && res.data.job_id) {
        const jobId = res.data.job_id;
        setActiveJobId(jobId);
        setPolling(true);
        setPollStatus('pending');
        setPollElapsed(0);
        startTimeRef.current = Date.now();

        // Elapsed timer
        elapsedRef.current = setInterval(() => {
          setPollElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);

        // Polling loop
        const MAX_POLL_MS = 600000; // 10 minutes
        pollRef.current = setInterval(async () => {
          try {
            const elapsed = Date.now() - startTimeRef.current;
            if (elapsed > MAX_POLL_MS) {
              stopPolling();
              setLoading(false);
              setError(`Job is taking longer than expected. The job_id is ${jobId} — you can check status manually at /api/v1/jobs/${jobId} using your API key.`);
              return;
            }
            const jobRes = await axios.get(`${API_URL}/api/v1/jobs/${jobId}`, { headers: { 'X-API-Key': apiKey } });
            const job = jobRes.data;
            setPollStatus(job.status);

            if (job.status === 'completed') {
              stopPolling();
              setResult(job.result);
              setLatency(Date.now() - start);
              setLoading(false);
            } else if (job.status === 'failed') {
              stopPolling();
              setError(job.error ? (typeof job.error === 'object' ? JSON.stringify(job.error) : job.error) : 'Job failed');
              setLatency(Date.now() - start);
              setLoading(false);
            } else if (job.status === 'cancelled') {
              stopPolling();
              setError('Job was cancelled');
              setLoading(false);
            }
          } catch {
            // Poll failed — keep trying unless timed out
          }
        }, 2000);

        return; // Don't setLoading(false) — polling handles it
      }

      // Synchronous response — display immediately
      setResult(res.data);
      setLatency(Date.now() - start);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      setError(typeof errDetail === 'object' ? (errDetail.message || JSON.stringify(errDetail)) : (errDetail || err.message));
      setLatency(Date.now() - start);
    }
    setLoading(false);
  }, [apiKey, stopPolling]);

  // Cleanup on unmount
  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  const statusHints = {
    pending: 'Queued — waiting for worker',
    running: 'Processing — typically 30-90 seconds for documents, longer for audio/video',
    completed: 'Done!',
    failed: 'Job failed',
    cancelled: 'Cancelled',
  };

  const RunBtn = ({ onClick, label }) => (
    <div className="space-y-2 mt-3">
      <Button data-testid="playground-run" onClick={onClick} disabled={loading || polling} className="bg-primary hover:bg-primary/90 w-full">
        {polling ? (
          <><Loader2 className="w-4 h-4 animate-spin mr-2" />Polling... ({pollElapsed}s)</>
        ) : loading ? (
          <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting...</>
        ) : (
          <><Play className="w-4 h-4 mr-2" />{label || 'Run'}</>
        )}
      </Button>
      {polling && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            {statusHints[pollStatus] || pollStatus}
          </span>
          <Button size="sm" variant="ghost" className="h-6 text-xs text-destructive" onClick={cancelJob}>Cancel</Button>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="playground-page" className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Playground</h2>
        <p className="text-muted-foreground mt-1 text-sm">Test any Tensornex service live. Pick a service, fill in the inputs, and hit Run to see the result.</p>
      </div>

      {/* API Key */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Key className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1">
              <Input data-testid="playground-api-key" type="password" placeholder="Paste your API key here (starts with gs_live_...)" value={apiKey} onChange={e => setApiKey(e.target.value)} className="font-mono text-sm" />
            </div>
            <Hint text="Your API key authenticates you. Go to Overview > API Keys to create one." />
          </div>
          {!apiKey && <p className="text-[10px] text-muted-foreground mt-2 ml-7">You need an API key to test services. Create one in your dashboard under API Keys.</p>}
        </CardContent>
      </Card>

      {/* Service Tabs */}
      <Tabs value={tab} onValueChange={v => { setTab(v); setResult(null); setError(null); }}>
        <TabsList className="bg-secondary/50 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="ml" className="text-xs gap-1"><Brain className="w-3 h-3" />ML Models</TabsTrigger>
          <TabsTrigger value="db" className="text-xs gap-1"><Database className="w-3 h-3" />Databases</TabsTrigger>
          <TabsTrigger value="storage" className="text-xs gap-1"><HardDrive className="w-3 h-3" />Storage</TabsTrigger>
          <TabsTrigger value="search" className="text-xs gap-1"><Search className="w-3 h-3" />AI & Search</TabsTrigger>
          <TabsTrigger value="pdf" className="text-xs gap-1"><FileText className="w-3 h-3" />PDF Parser</TabsTrigger>
          <TabsTrigger value="docling" className="text-xs gap-1"><Sparkles className="w-3 h-3" />Docling</TabsTrigger>
        </TabsList>

        {/* ═══════ ML MODELS ═══════ */}
        <TabsContent value="ml"><MLPanel hasSvc={hasSvc} call={call} loading={loading} RunBtn={RunBtn} /></TabsContent>

        {/* ═══════ DATABASES ═══════ */}
        <TabsContent value="db"><DatabasePanel hasSvc={hasSvc} call={call} loading={loading} RunBtn={RunBtn} /></TabsContent>

        {/* ═══════ STORAGE ═══════ */}
        <TabsContent value="storage"><StoragePanel hasSvc={hasSvc} call={call} apiKey={apiKey} loading={loading} RunBtn={RunBtn} /></TabsContent>

        {/* ═══════ AI & SEARCH ═══════ */}
        <TabsContent value="search"><SearchPanel hasSvc={hasSvc} call={call} loading={loading} RunBtn={RunBtn} /></TabsContent>

        {/* ═══════ PDF PARSER ═══════ */}
        <TabsContent value="pdf"><PdfPanel hasSvc={hasSvc} call={call} apiKey={apiKey} loading={loading} RunBtn={RunBtn} /></TabsContent>

        {/* ═══════ DOCLING ═══════ */}
        <TabsContent value="docling"><DoclingPanel hasSvc={hasSvc} call={call} apiKey={apiKey} loading={loading} RunBtn={RunBtn} /></TabsContent>
      </Tabs>

      {/* Output */}
      <OutputViewer data={result} latency={latency} error={error} />
    </div>
  );
}


// ════════════════════════════════════════════════
// ML MODELS PANEL
// ════════════════════════════════════════════════
function MLPanel({ hasSvc, call, loading, RunBtn }) {
  const [sub, setSub] = useState('embed');
  const [embedTexts, setEmbedTexts] = useState('What is machine learning?\nDeep learning basics');
  const [embedModel, setEmbedModel] = useState('bge_m3');
  const [rerankQuery, setRerankQuery] = useState('neural networks');
  const [rerankPassages, setRerankPassages] = useState('Neural nets use layers of neurons\nTrees grow in forests\nDeep learning is a subset of ML');
  const [rerankTopK, setRerankTopK] = useState(3);
  const [nlpText, setNlpText] = useState('Apple Inc. released iPhone 15 in Cupertino, California.');

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">ML Models — Text Analysis & Embeddings</CardTitle>
        <p className="text-[10px] text-muted-foreground">These models analyze text. Embeddings convert text to numbers for search. Reranker sorts results by relevance. NLP extracts names, places, and entities.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant={sub === 'embed' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSub('embed')}>Embed Text</Button>
          <Button size="sm" variant={sub === 'rerank' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSub('rerank')}>Rerank</Button>
          <Button size="sm" variant={sub === 'nlp' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSub('nlp')}>NLP (Entities)</Button>
        </div>

        {sub === 'embed' && (
          !hasSvc('bge_m3') && !hasSvc('minilm') ? <ServiceLocked name="BGE-M3 or MiniLM" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Type some sentences below. The model will convert each sentence into a vector (a list of numbers). These vectors are used to find similar text in search.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="Choose BGE-M3 for high accuracy (1024 numbers per sentence) or MiniLM for speed (384 numbers).">Model</FieldLabel>
              <Select value={embedModel} onValueChange={setEmbedModel}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bge_m3">BGE-M3 (1024 dimensions — best quality)</SelectItem>
                  <SelectItem value="minilm">MiniLM (384 dimensions — fastest)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="Enter one sentence per line. Each will be converted to a vector.">Texts (one per line)</FieldLabel>
              <textarea className="w-full h-20 bg-background border border-input rounded-md p-2.5 text-xs font-mono resize-none" value={embedTexts} onChange={e => setEmbedTexts(e.target.value)} />
            </div>
            <RunBtn onClick={() => call('POST', '/embeddings/embed', { texts: embedTexts.split('\n').filter(Boolean), model: embedModel })} label="Generate Embeddings" />
            <p className="text-[10px] text-muted-foreground">You'll get back: A vector (list of numbers) for each sentence. Store these in Qdrant for semantic search.</p>
          </div>)
        )}

        {sub === 'rerank' && (
          !hasSvc('bge_reranker') ? <ServiceLocked name="BGE Reranker" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Got search results but want to sort them by how relevant they really are? Enter your question and the results — the reranker will score and sort them.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="The question you're searching for.">Query</FieldLabel>
              <Input className="h-8 text-xs" value={rerankQuery} onChange={e => setRerankQuery(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="Candidate results — one per line. The reranker will score each one.">Passages (one per line)</FieldLabel>
              <textarea className="w-full h-20 bg-background border border-input rounded-md p-2.5 text-xs font-mono resize-none" value={rerankPassages} onChange={e => setRerankPassages(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="How many top results to return.">Top K</FieldLabel>
              <Input type="number" className="h-8 text-xs w-20" value={rerankTopK} onChange={e => setRerankTopK(e.target.value)} min={1} max={100} />
            </div>
            <RunBtn onClick={() => call('POST', '/embeddings/rerank', { query: rerankQuery, passages: rerankPassages.split('\n').filter(Boolean), top_k: parseInt(rerankTopK) })} label="Rerank Passages" />
            <p className="text-[10px] text-muted-foreground">You'll get back: Each passage with a relevance score (0-1). Higher = more relevant to your query.</p>
          </div>)
        )}

        {sub === 'nlp' && (
          !hasSvc('spacy') ? <ServiceLocked name="spaCy NLP" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Paste any text and the model will find names of people, companies, locations, dates, and more. Great for extracting structured data from unstructured text.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="Any text — a news article, an email, a document. The model will find entities in it.">Text</FieldLabel>
              <textarea className="w-full h-20 bg-background border border-input rounded-md p-2.5 text-xs font-mono resize-none" value={nlpText} onChange={e => setNlpText(e.target.value)} />
            </div>
            <RunBtn onClick={() => call('POST', '/embeddings/nlp', { text: nlpText })} label="Extract Entities" />
            <p className="text-[10px] text-muted-foreground">You'll get back: A list of entities (people, organizations, places, dates) found in your text with their labels.</p>
          </div>)
        )}
      </CardContent>
    </Card>
  );
}


// ════════════════════════════════════════════════
// DATABASES PANEL
// ════════════════════════════════════════════════
function DatabasePanel({ hasSvc, call, loading, RunBtn }) {
  const [svc, setSvc] = useState('qdrant');
  const svcMap = {
    qdrant: { name: 'Qdrant', id: 'qdrant', desc: 'Vector database for semantic search. This test creates a small collection, stores a vector, searches it, and cleans up.' },
    elasticsearch: { name: 'Elasticsearch', id: 'elasticsearch', desc: 'Full-text search engine. This test creates an index, adds a document, searches for it, and shows the result.' },
    neo4j: { name: 'Neo4j', id: 'neo4j', desc: 'Graph database for relationships. This test creates a node and reads it back.' },
    redis: { name: 'Redis', id: 'redis', desc: 'In-memory cache. This test stores a value, reads it back, and confirms it matches.' },
    kafka: { name: 'Kafka', id: 'kafka', desc: 'Event streaming. This test lists available topics to confirm the connection works.' },
  };
  const info = svcMap[svc];

  const runTest = async (apiCall) => {
    apiCall();
  };

  const tests = {
    qdrant: {
      quick: { label: 'Test: List Collections', desc: 'Lists all your vector collections. If the service is working, you will see a list (may be empty if you have not created any yet).', run: () => call('GET', '/vectors/collections') },
      full: {
        label: 'Test: Create + Store + Search',
        desc: 'Creates a temporary collection, stores a test vector, searches for it, and shows results. This proves the full Qdrant pipeline works end-to-end.',
        run: async () => {
          const name = `test_${Date.now().toString(36)}`;
          const vec = Array.from({length: 1024}, () => Math.round((Math.random() * 2 - 1) * 100) / 100);
          // We'll call create, and let the user see the result. For a full e2e test, they can follow the steps.
          call('POST', '/vectors/collections', { collection_name: name, vector_size: 1024, distance: "cosine" });
        }
      }
    },
    elasticsearch: {
      quick: { label: 'Test: Create Index', desc: 'Creates a test search index. If successful, the service is connected and working.', run: () => call('POST', '/search/indices', { index_name: `test_${Date.now().toString(36)}`, mappings: { properties: { title: { type: "text" }, content: { type: "text" } } } }) },
      full: { label: 'Test: Index + Search Document', desc: 'Creates an index, adds a sample document, and searches for it.', run: () => {
        const idx = `test_${Date.now().toString(36)}`;
        call('POST', '/search/indices', { index_name: idx, mappings: { properties: { content: { type: "text" } } } });
      }}
    },
    neo4j: {
      quick: { label: 'Test: Create Node', desc: 'Creates a test node in the graph database and reads it back. If successful, Neo4j is connected.', run: () => call('POST', '/graph/query', { cypher: "CREATE (n:PlaygroundTest {name: $name, created: $time}) RETURN n", parameters: { name: "test_node", time: new Date().toISOString() } }) },
      full: { label: 'Test: Query All Nodes', desc: 'Lists all nodes in your graph. Shows what data you have stored.', run: () => call('POST', '/graph/query', { cypher: "MATCH (n) RETURN n LIMIT 10", parameters: {} }) }
    },
    redis: {
      quick: { label: 'Test: Store & Read Value', desc: 'Stores a test value in the cache and reads it back. If you see the value returned, Redis is working.', run: () => call('POST', '/cache/set', { key: `playground_test_${Date.now()}`, value: { message: "Hello from Tensornex Playground!", tested_at: new Date().toISOString() }, ttl_seconds: 60 }) },
      full: { label: 'Test: List Keys', desc: 'Lists all your cached keys. Shows what data you have stored in Redis.', run: () => call('POST', '/cache/keys', { pattern: "*" }) }
    },
    kafka: {
      quick: { label: 'Test: List Topics', desc: 'Lists all your streaming topics. If the service is working, you will see a list (may be empty).', run: () => call('GET', '/streaming/topics') },
      full: { label: 'Test: Create Topic', desc: 'Creates a new streaming topic. If successful, Kafka is connected.', run: () => call('POST', '/streaming/topics', { topic: `test_${Date.now().toString(36)}`, num_partitions: 1 }) }
    }
  };

  const svcTests = tests[svc] || {};

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Databases — Test Your Connection</CardTitle>
        <p className="text-[10px] text-muted-foreground">Select a database and click a test button. No setup needed — each test runs a safe operation to verify the service is working.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(svcMap).map(([k, v]) => (
            <Button key={k} size="sm" variant={svc === k ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSvc(k)}>{v.name}</Button>
          ))}
        </div>
        {!hasSvc(info.id) ? <ServiceLocked name={info.name} /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">{info.desc}</p>
            {Object.entries(svcTests).map(([k, test]) => (
              <div key={k} className="p-3 rounded-lg border border-border/30 space-y-2">
                <p className="text-xs text-muted-foreground">{test.desc}</p>
                <Button data-testid={`test-${svc}-${k}`} onClick={test.run} disabled={loading} className="bg-primary hover:bg-primary/90 w-full">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  {test.label}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ════════════════════════════════════════════════
// STORAGE PANEL
// ════════════════════════════════════════════════
function StoragePanel({ hasSvc, call, apiKey, loading, RunBtn }) {
  const [action, setAction] = useState('upload');
  const [bucket, setBucket] = useState('my-documents');
  const [objectName, setObjectName] = useState('');
  const [prefix, setPrefix] = useState('');

  if (!hasSvc('minio')) return <Card className="bg-card border-border/50 mt-3"><CardContent className="p-5"><ServiceLocked name="MinIO Storage" /></CardContent></Card>;

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Storage (MinIO) — Upload & Download Files</CardTitle>
        <p className="text-[10px] text-muted-foreground">Store files in the cloud. Upload documents, images, audio — anything. Files are private to your account. Other users cannot access them.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant={action === 'upload' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('upload')}>Upload File</Button>
          <Button size="sm" variant={action === 'list' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('list')}>List Files</Button>
          <Button size="sm" variant={action === 'bucket' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('bucket')}>Create Bucket</Button>
        </div>

        {action === 'upload' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Upload a file to your private storage. You can then use the file URL with other services like Docling (document conversion) or PDF Parser.</p>
            <FileDropZone apiKey={apiKey} onFileReady={(url, name) => { setObjectName(name); toast.success(`File "${name}" uploaded!`); }} label="Drag & drop any file here" />
            <p className="text-[10px] text-muted-foreground">After upload, the file is stored in your private bucket. You can use it with Docling, PDF Parser, or download it later.</p>
          </div>
        )}

        {action === 'list' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">See all files in a bucket. A bucket is like a folder for your files.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="The name of your bucket (folder). Default: my-documents">Bucket Name</FieldLabel>
              <Input className="h-8 text-xs" value={bucket} onChange={e => setBucket(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="Optional: filter files by path prefix (e.g., 'reports/' shows only files in that folder)">Prefix (optional)</FieldLabel>
              <Input className="h-8 text-xs" value={prefix} onChange={e => setPrefix(e.target.value)} placeholder="e.g., reports/" />
            </div>
            <RunBtn onClick={() => call('POST', '/storage/list', { bucket_name: bucket, prefix, max_keys: 100 })} label="List Files" />
          </div>
        )}

        {action === 'bucket' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Create a new bucket (folder) to organize your files.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="Name for your new bucket. Use lowercase, hyphens allowed.">Bucket Name</FieldLabel>
              <Input className="h-8 text-xs" value={bucket} onChange={e => setBucket(e.target.value)} />
            </div>
            <RunBtn onClick={() => call('POST', '/storage/buckets', { bucket_name: bucket })} label="Create Bucket" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ════════════════════════════════════════════════
// AI & SEARCH PANEL
// ════════════════════════════════════════════════
function SearchPanel({ hasSvc, call, loading, RunBtn }) {
  const [svc, setSvc] = useState('vane');
  const [query, setQuery] = useState('What are the latest AI regulations in California?');
  const [mode, setMode] = useState('balanced');
  const [url, setUrl] = useState('https://example.com');
  const [limit, setLimit] = useState(10);

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">AI & Search — Web Search, AI Answers, Page Reading</CardTitle>
        <p className="text-[10px] text-muted-foreground">Search the web, get AI-researched answers, or read any web page — all through simple API calls.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant={svc === 'vane' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSvc('vane')}>AI Search</Button>
          <Button size="sm" variant={svc === 'searxng' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSvc('searxng')}>Web Search</Button>
          <Button size="sm" variant={svc === 'browser' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setSvc('browser')}>Read Web Page</Button>
        </div>

        {svc === 'vane' && (!hasSvc('vane_search') ? <ServiceLocked name="AI Search (Vane)" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Ask any question. The AI will search the web, read multiple sources, and give you a researched answer with citations.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="Your question — the AI will research this across multiple web sources.">Question</FieldLabel>
              <Input className="h-8 text-xs" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="Speed = faster but less thorough. Quality = slower but reads more sources. Balanced = best of both.">Mode</FieldLabel>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="speed">Speed (fast, fewer sources)</SelectItem>
                  <SelectItem value="balanced">Balanced (recommended)</SelectItem>
                  <SelectItem value="quality">Quality (thorough, more sources)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RunBtn onClick={() => call('POST', '/search/ai-query', { query, mode })} label="Ask AI" />
            <p className="text-[10px] text-muted-foreground">You'll get back: An AI-written answer with links to the sources it used.</p>
          </div>
        ))}

        {svc === 'searxng' && (!hasSvc('searxng') ? <ServiceLocked name="Web Search (SearXNG)" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Search across Google, Bing, DuckDuckGo, and 70+ other search engines in one call. Get raw results with titles, URLs, and snippets.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="What to search for.">Search Query</FieldLabel>
              <Input className="h-8 text-xs" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel hint="How many results to return.">Max Results</FieldLabel>
              <Input type="number" className="h-8 text-xs w-20" value={limit} onChange={e => setLimit(e.target.value)} min={1} max={50} />
            </div>
            <RunBtn onClick={() => call('POST', '/search/raw', { query, limit: parseInt(limit) })} label="Search Web" />
          </div>
        ))}

        {svc === 'browser' && (!hasSvc('lightpanda_browser') ? <ServiceLocked name="Headless Browser" /> : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Enter any website URL and the browser will load the full page (including JavaScript) and give you the text content. Works even on pages that regular scrapers can't read.</p>
            <div className="space-y-1.5">
              <FieldLabel hint="The full URL of the page you want to read (e.g., https://example.com/article)">Page URL</FieldLabel>
              <Input className="h-8 text-xs" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." />
            </div>
            <RunBtn onClick={() => call('POST', '/browse/render', { url, wait_for: 'networkidle', timeout_ms: 30000 })} label="Read Page" />
            <p className="text-[10px] text-muted-foreground">You'll get back: The page title and full text content, ready to process.</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


// ════════════════════════════════════════════════
// PDF PARSER PANEL (Legacy)
// ════════════════════════════════════════════════
function PdfPanel({ hasSvc, call, apiKey, loading, RunBtn }) {
  const [action, setAction] = useState('parse');
  const [url, setUrl] = useState('');

  if (!hasSvc('pdf_parser')) return <Card className="bg-card border-border/50 mt-3"><CardContent className="p-5"><ServiceLocked name="PDF Parser" /></CardContent></Card>;

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">PDF Parser (Legacy) — Extract Text & Tables from PDFs</CardTitle>
        <p className="text-[10px] text-muted-foreground">Parse PDF files to extract text, tables, and metadata. For the newer, more powerful document processing, use the Docling tab instead.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant={action === 'parse' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('parse')}>Parse PDF</Button>
          <Button size="sm" variant={action === 'ocr' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('ocr')}>OCR (Scanned)</Button>
          <Button size="sm" variant={action === 'tables' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('tables')}>Extract Tables</Button>
        </div>

        <div className="space-y-3">
          <FileDropZone apiKey={apiKey} onFileReady={(fileUrl) => setUrl(fileUrl)} accept=".pdf" label="Upload a PDF, or paste a URL below" />
          <div className="space-y-1.5">
            <FieldLabel hint="A direct URL to a PDF file. If you uploaded above, this is filled automatically.">PDF URL</FieldLabel>
            <Input className="h-8 text-xs font-mono" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/document.pdf or presigned URL" />
          </div>

          {action === 'parse' && (
            <>
              <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Extracts text from each page of a normal (non-scanned) PDF. Fast and accurate for digitally-created documents.</p>
              <RunBtn onClick={() => call('POST', '/pdf/parse', { url })} label="Parse PDF" />
              <p className="text-[10px] text-muted-foreground">You'll get back: Page count, text per page, and document metadata (author, title).</p>
            </>
          )}
          {action === 'ocr' && (
            <>
              <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Uses OCR (optical character recognition) for scanned PDFs — documents that are essentially photos of text. Slower but works on scanned receipts, letters, etc.</p>
              <RunBtn onClick={() => call('POST', '/pdf/parse-ocr', { url })} label="OCR Parse" />
              <p className="text-[10px] text-muted-foreground">You'll get back: Extracted text elements with type labels and confidence scores.</p>
            </>
          )}
          {action === 'tables' && (
            <>
              <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">Finds and extracts tables from a PDF as structured data. Great for financial reports, invoices, or any document with tabular data.</p>
              <RunBtn onClick={() => call('POST', '/pdf/extract-tables', { url })} label="Extract Tables" />
              <p className="text-[10px] text-muted-foreground">You'll get back: Tables as structured data with rows and columns, plus HTML rendering.</p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ════════════════════════════════════════════════
// DOCLING PANEL (Universal Document AI)
// ════════════════════════════════════════════════
function DoclingPanel({ hasSvc, call, apiKey, loading, RunBtn }) {
  const [action, setAction] = useState('convert');
  const [url, setUrl] = useState('');
  const [outputFormat, setOutputFormat] = useState('markdown');
  const [maxTokens, setMaxTokens] = useState([512]);
  const [mergePeers, setMergePeers] = useState(true);

  if (!hasSvc('docling')) return <Card className="bg-card border-border/50 mt-3"><CardContent className="p-5"><ServiceLocked name="Docling (Document AI)" /></CardContent></Card>;

  const formatDesc = {
    json: 'Full structured data with all details (tables, images, confidence scores, bounding boxes)',
    markdown: 'Clean readable text — perfect for feeding to AI models or reading',
    html: 'Formatted HTML with rendered tables — good for display',
    text: 'Plain text only — lightest, no formatting',
    doctags: 'Special format for AI training — preserves layout information',
  };

  return (
    <Card className="bg-card border-border/50 mt-3">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Docling — Universal Document AI</CardTitle>
        <p className="text-[10px] text-muted-foreground">The most powerful document processor. Handles 20+ file formats: PDFs, Word docs, PowerPoints, Excel, images, audio, video, and more. Upload any file and get structured content back.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant={action === 'convert' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('convert')}>Convert Document</Button>
          <Button size="sm" variant={action === 'ocr' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('ocr')}>OCR (Scanned)</Button>
          <Button size="sm" variant={action === 'chunk' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('chunk')}>Chunk for RAG</Button>
          <Button size="sm" variant={action === 'vlm' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('vlm')}>VLM (Visual AI)</Button>
          <Button size="sm" variant={action === 'transcribe' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('transcribe')}>Transcribe</Button>
          <Button size="sm" variant={action === 'diarize' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('diarize')}>Transcribe + Speakers</Button>
          <Button size="sm" variant={action === 'metadata' ? 'default' : 'outline'} className="text-xs h-7" onClick={() => setAction('metadata')}>Extract Metadata</Button>
        </div>

        {/* Shared file upload + URL input */}
        <FileDropZone
          apiKey={apiKey}
          onFileReady={(fileUrl) => setUrl(fileUrl)}
          accept={action === 'transcribe' || action === 'diarize' ? '.wav,.mp3,.m4a,.aac,.ogg,.flac,.mp4,.avi,.mov' : '.pdf,.docx,.pptx,.xlsx,.html,.csv,.md,.tex,.png,.jpg,.jpeg,.tiff,.bmp,.webp'}
          label={action === 'transcribe' || action === 'diarize' ? 'Upload an audio or video file' : 'Upload a document (PDF, Word, PowerPoint, Excel, image, etc.)'}
        />
        <div className="space-y-1.5">
          <FieldLabel hint="A direct URL to your file. If you uploaded above, paste the presigned URL here. You can also use any public URL.">File URL</FieldLabel>
          <Input className="h-8 text-xs font-mono" value={url} onChange={e => setUrl(e.target.value)} placeholder="Paste URL here (presigned or public)" />
        </div>

        {action === 'convert' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              Upload any document — PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx), HTML page, CSV, Markdown, LaTeX, or images.
              Docling will read it with AI, detect the layout (text blocks, tables, images, code, formulas), and return structured content.
            </p>
            <div className="space-y-1.5">
              <FieldLabel hint={formatDesc[outputFormat]}>Output Format</FieldLabel>
              <Select value={outputFormat} onValueChange={setOutputFormat}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="markdown">Markdown (recommended — clean & readable)</SelectItem>
                  <SelectItem value="json">JSON (full structured data)</SelectItem>
                  <SelectItem value="html">HTML (with rendered tables)</SelectItem>
                  <SelectItem value="text">Plain Text (lightest)</SelectItem>
                  <SelectItem value="doctags">DocTags (for AI training)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RunBtn onClick={() => call('POST', '/docs/convert', { url, output_format: outputFormat })} label="Convert Document" />
            <p className="text-[10px] text-muted-foreground">You'll get back: Structured content with page count, detected elements (paragraphs, tables, images), confidence scores, and your chosen format.</p>
          </div>
        )}

        {action === 'ocr' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              For scanned documents or photos of text — receipts, handwritten notes, old letters, scanned contracts.
              The AI runs full-page OCR to read every word, even from blurry or rotated images. Each detected text element gets a confidence score so you know how reliable the reading is.
            </p>
            <RunBtn onClick={() => call('POST', '/docs/convert-ocr', { url, output_format: 'json' })} label="OCR Scan Document" />
            <p className="text-[10px] text-muted-foreground">You'll get back: Every detected text element with type, content, page number, and confidence score (0-100%). Green = high confidence, yellow = check it, red = may need manual review.</p>
          </div>
        )}

        {action === 'chunk' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              Building a search system or AI chatbot? This splits your document into smart chunks — each chunk is the right size for an AI model to process, and includes the section headings for context.
              Perfect for RAG (Retrieval-Augmented Generation) pipelines.
            </p>
            <div className="space-y-1.5">
              <FieldLabel hint="Maximum tokens (words) per chunk. Smaller = more precise search results. Larger = more context per result. 512 is a good default.">Max Tokens per Chunk: {maxTokens[0]}</FieldLabel>
              <Slider value={maxTokens} onValueChange={setMaxTokens} min={128} max={2048} step={64} className="py-2" />
              <div className="flex justify-between text-[9px] text-muted-foreground"><span>128 (small, precise)</span><span>2048 (large, more context)</span></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={mergePeers} onCheckedChange={setMergePeers} />
              <span className="text-xs text-muted-foreground">Merge small neighboring chunks together</span>
              <Hint text="When enabled, very short chunks under the same heading are combined into one. Usually you want this on." />
            </div>
            <RunBtn onClick={() => call('POST', '/docs/chunk', { url, max_tokens: maxTokens[0], merge_peers: mergePeers })} label="Convert & Chunk" />
            <p className="text-[10px] text-muted-foreground">You'll get back: A list of chunks, each with text content, section headings (for context), and page number. Feed these into embeddings + Qdrant for search.</p>
          </div>
        )}

        {action === 'vlm' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              For complex PDFs with charts, diagrams, mixed text-and-image layouts, or unusual formatting.
              A Vision Language Model (AI that can "see") reads each page as an image and understands the visual layout. Slower but much better for visually complex documents.
              Note: This can take several minutes for large documents.
            </p>
            <div className="space-y-1.5">
              <FieldLabel>Output Format</FieldLabel>
              <Select value={outputFormat} onValueChange={setOutputFormat}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="markdown">Markdown</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="doctags">DocTags</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RunBtn onClick={() => call('POST', '/docs/convert-vlm', { url, output_format: outputFormat })} label="Convert with Vision AI (may take minutes)" />
            <p className="text-[10px] text-muted-foreground">You'll get back: Visually-understood content. Best for charts, mixed layouts, and documents where standard conversion misses things.</p>
          </div>
        )}

        {action === 'transcribe' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              Upload a recording — meeting, podcast, voicemail, interview, or video.
              The AI (Whisper Turbo) will listen to it and convert speech to text with timestamps.
              Supports: WAV, MP3, M4A, AAC, OGG, FLAC (audio) and MP4, AVI, MOV (video — audio track is extracted automatically).
            </p>
            <RunBtn onClick={() => call('POST', '/docs/transcribe', { url, output_format: 'markdown' })} label="Transcribe Audio/Video" />
            <p className="text-[10px] text-muted-foreground">Audio/video transcription typically takes 1-3 minutes depending on file length.</p>
            <p className="text-[10px] text-muted-foreground">You'll get back: Timestamped transcript — each paragraph shows when it was spoken. Ready for search, summarization, or note-taking.</p>
          </div>
        )}

        {action === 'diarize' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              Like regular transcription, but also identifies WHO is speaking. Each word and sentence gets a speaker label (SPEAKER_00, SPEAKER_01, etc.) plus word-level timestamps and confidence scores.
              Perfect for: meeting transcripts, interviews, podcasts, call recordings.
            </p>
            <RunBtn onClick={() => call('POST', '/docs/transcribe-diarized', { url, output_format: 'text' })} label="Transcribe with Speaker Detection" />
            <p className="text-[10px] text-muted-foreground">Speaker detection typically takes 1-3 minutes. Results include speaker labels and word-level timestamps.</p>
            <p className="text-[10px] text-muted-foreground">You'll get back: Segments with speaker labels, word-level timestamps, and confidence scores per word. Speakers are color-coded in the output.</p>
          </div>
        )}

        {action === 'metadata' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/10">
              Extract format-specific metadata that goes beyond content conversion.
              PDF: bookmarks, link annotations, form fields, creation date, author.
              PPTX: slide count, speaker notes.
              DOCX: tracked changes, comments, revision history.
              HTML: all links, images, headings, embedded media (iframes, videos).
            </p>
            <RunBtn onClick={() => call('POST', '/docs/extract-metadata', { url })} label="Extract Metadata" />
            <p className="text-[10px] text-muted-foreground">You'll get back: Format-specific metadata — bookmarks, links, form fields, speaker notes, tracked changes, embedded media, depending on the file type.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
